import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { SLOT_LABEL } from '../../lib/plans';
import { formatReminderTime, scheduleKeys } from '../../lib/schedule';
import { BellIcon } from '../icons';
import { Section } from '../Section';

const DAYS_AHEAD = 2;

/**
 * Dashboard card: reminders due today and tomorrow ("Move the chicken
 * thighs to the fridge"), tickable in place. Hidden when there are none, so
 * households that never use reminders don't see an empty box.
 */
export function RemindersCard() {
  const qc = useQueryClient();
  const reminders = useQuery({
    queryKey: scheduleKeys.upcomingReminders(DAYS_AHEAD),
    queryFn: () => api.reminders.upcoming(DAYS_AHEAD),
  });
  const toggle = useMutation({
    mutationFn: (v: { planId: string; id: string; done: boolean }) =>
      api.reminders.update(v.planId, v.id, { done: v.done }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['plans', 'reminders'] }),
  });

  const items = reminders.data?.reminders ?? [];
  if (!reminders.isError && items.length === 0) return null;

  return (
    <Section
      id="upcoming-reminders-heading"
      title="Reminders"
      accent="orange"
      icon={<BellIcon className="size-5" />}
    >
      {reminders.isError ? <p className="text-sm text-fail">Couldn’t load reminders.</p> : null}
      {toggle.isError ? <p className="text-sm text-fail">Couldn’t update that.</p> : null}
      <ul className="flex flex-col divide-y divide-line">
        {items.map((r) => {
          const done =
            toggle.isPending && toggle.variables.id === r.id
              ? toggle.variables.done
              : r.doneAt !== null;
          return (
            <li key={r.id} className="flex items-start gap-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 size-6 shrink-0 cursor-pointer accent-brand"
                checked={done}
                aria-label={`Done: ${r.text}`}
                onChange={(e) =>
                  toggle.mutate({ planId: r.mealPlanId, id: r.id, done: e.target.checked })
                }
              />
              <div className="min-w-0 flex-1">
                <p className={done ? 'text-sm text-muted line-through' : 'text-sm font-medium'}>
                  {r.text}
                </p>
                <p className="text-xs text-muted">
                  {formatReminderTime(r.remindAt)} · for{' '}
                  <Link
                    to={`/plan/${r.plan.id}`}
                    className="text-brand underline-offset-2 hover:underline"
                  >
                    {r.plan.recipeTitles.length
                      ? r.plan.recipeTitles.join(' + ')
                      : SLOT_LABEL[r.plan.slot].toLowerCase()}
                  </Link>
                </p>
              </div>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
